import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/form';
import { notify } from '@/shared/lib/toast';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { useUpdateBillingEmail } from '../hooks/use-update-billing-email';
import {
  BILLING_EMAIL_DEFAULTS,
  type BillingEmailValues,
  billingEmailSchema,
} from '../schemas/billing-email-schema';

type BillingEmailFormProps = { currentEmail: string; onDone: () => void };

export function BillingEmailForm({ currentEmail, onDone }: BillingEmailFormProps) {
  const form = useForm<BillingEmailValues>({
    resolver: zodResolver(billingEmailSchema),
    defaultValues: { ...BILLING_EMAIL_DEFAULTS, email: currentEmail },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { handleSubmit, register, setError, formState } = form;
  const { updateBillingEmail } = useUpdateBillingEmail();

  const onSubmit = async ({ email }: BillingEmailValues) => {
    const { errorCode } = await updateBillingEmail(email);
    if (errorCode) {
      setError('email', {
        type: 'server',
        message: 'Paddle did not accept this address. Check it and try again.',
      });
      return;
    }
    notify.success('Billing email saved. Paddle sends the next invoice there.');
    onDone();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex w-full flex-col gap-2">
        <FormField name="email">
          <FormItem className="gap-2">
            <FormLabel className="sr-only">Billing email</FormLabel>
            <div className="flex flex-wrap gap-2">
              <FormControl>
                <Input
                  type="email"
                  autoComplete="email"
                  className="w-auto shrink grow basis-60"
                  {...register('email')}
                />
              </FormControl>
              <Button
                type="submit"
                variant="primary"
                size="field"
                isLoading={formState.isSubmitting}
              >
                Save
              </Button>
              <Button variant="ghost" size="field" onClick={onDone}>
                Cancel
              </Button>
            </div>
            <FormMessage />
          </FormItem>
        </FormField>
      </form>
    </FormProvider>
  );
}
